import { useRouter } from "next/router";
import React, { useEffect } from "react";
import Breadcrumbs from "@/utils/Breadcrumbs";
import SocialCounter from "../SocialCounter";
import { useState, useRef } from "react";
import Link from "next/link";
import { GetServerSideProps } from "next";
import PlatformsChart from "../PlatformsChart";
import Head from "next/head";
import BreadcrumbSchema from "@/schema/BreadcrumbSchema";
import { toPng } from "html-to-image";


export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug, month, year } = context.params || {}; // Extract parameters from context
  const API_URL = `${process.env.NEXT_PUBLIC_MAIN_API_URL}/b2b/get-all-country`;

  const toCamelCaseWithSpaces = (str: string) => {
    if (!str) return "";
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formattedSlug = Array.isArray(slug)
    ? slug[0]
    : typeof slug === "string"
    ? slug
    : "";
  const formattedMonth = Array.isArray(month)
    ? month[0]
    : typeof month === "string"
    ? month
    : "";
  const formattedYear = Array.isArray(year)
    ? year[0]
    : typeof year === "string"
    ? year
    : "";
  const formattedCountryName = toCamelCaseWithSpaces(formattedSlug);

  const monthParts = formattedMonth.split("-");
  const [part1, part2, part3, part4, part5, part6] = monthParts;
  const formattedMonthName = toCamelCaseWithSpaces(part5);

  const monthMap: { [key: string]: string } = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };

  const formattedMonthString = `${part6}-${monthMap[part5] || "00"}`;
  let countryStats = null;
  let error = null;

  try {
    // Fetch all countries
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      const selectedCountry = data.data.c_l.find(
        (country: Country) => country.n === formattedCountryName
      );
      // console.log(selectedCountry)
      if (selectedCountry) {
        const statsResponse = await fetch(
          `${process.env.SOCIAL_STATS_API_URL}/api?type=monthly&countryId=${selectedCountry.i}&month=${formattedMonthString}`
        );
        // console.log(statsResponse)
        if (!statsResponse.ok) {
          throw new Error(
            `HTTP error while fetching stats! status: ${statsResponse.status}`
          );
        }

        countryStats = await statsResponse.json();
      } else {
        error = "Country not found";
      }
    } else {
      error = "Failed to fetch countries";
    }
  } catch (err: any) {
    error = err.message;
    console.error("Fetch error:", err);
  }

  return {
    props: {
      countryStats,
      error,
    },
  };
};

interface Country {
  s: string; // slug
  i: number; // index
  n: string; // name
}

interface CountryStats {
  facebookStats: any; // Define the exact type according to your API response
  instagramStats: any;
  messengerStats: any;
  linkedinStats: any;
}

interface StatsDetailPageProps {
  countryStats: CountryStats | null;
  error: string | null;
}

const StatsDetail = ({ countryStats, error }: StatsDetailPageProps) => {
  const router = useRouter();
  const { slug, month, year } = router.query;

  const toCamelCaseWithSpaces = (str: string) => {
    if (!str) return "";
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formattedSlug = Array.isArray(slug)
    ? slug[0]
    : typeof slug === "string"
    ? slug
    : "";
  const formattedMonth = Array.isArray(month)
    ? month[0]
    : typeof month === "string"
    ? month
    : "";
  const formattedYear = Array.isArray(year)
    ? year[0]
    : typeof year === "string"
    ? year
    : "";
  const formattedCountryName = toCamelCaseWithSpaces(formattedSlug);

  const monthParts = formattedMonth.split("-");
  const [part1, part2, part3, part4, part5, part6] = monthParts;
  const formattedMonthName = toCamelCaseWithSpaces(part5);

  const monthMap: { [key: string]: string } = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };

  const formattedMonthString = `${part6}-${monthMap[part5] || "00"}`;

  const location = [
    { text: "Home", url: "/" },
    { text: "Stats", url: "/stats" },
    {
      text: `${formattedCountryName}`,
      url: `/stats/social-media-users-in-${formattedSlug}`,
    },
    { text: `${formattedMonthName}` },
  ];


  
  const breadcrumbItems = [
    { name: "Home", url: "https://www.bizdataset.com/" },
    // Add more breadcrumb items as needed
    { name: "Stats", url: "https://www.bizdataset.com/stats" },
    { name: `${formattedCountryName}`, url: `https://www.bizdataset.com/stats/social-media-users-in-${formattedSlug}` },
    { name: `${formattedMonthName}`, url: `https://www.bizdataset.com/stats/${formattedSlug}/social-media-users-in-${part5}-${part6}` },
  ];

  const formatNumberWithSpaces = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // Format with spaces
  };

  const messages = [
    <>
              <div className="text-center">{formatNumberWithSpaces(countryStats?.facebookStats?.total || 0)} </div>
      <span className="gradient-new-text">Facebook users</span> in {formattedCountryName || "Country"}
    </>,
    <>
          <div className="text-center"> {formatNumberWithSpaces(countryStats?.instagramStats?.total || 0)}</div>
          <span className="gradient-new-text">Instagram users</span> in {formattedCountryName || "Country"}
    </>,
    <>
          <div className="text-center">{formatNumberWithSpaces(countryStats?.messengerStats?.total || 0)}</div>
          <span className="gradient-new-text">Messenger users</span> in {formattedCountryName || "Country"}
    </>,
    <>
          <div className="text-center">{formatNumberWithSpaces(countryStats?.linkedinStats?.total || 0)} </div>
          <span className="gradient-new-text">LinkedIn users</span> in {formattedCountryName || "Country"}
    </>,
  ];

  const totalRecords = countryStats?.facebookStats?.total ?? 0;
  const totalPhoneNumbers = countryStats?.instagramStats?.total ?? 0;
  const totalEmails = countryStats?.messengerStats?.total ?? 0;
  const totalFacebooks = countryStats?.linkedinStats?.total ?? 0;

  const page1Icons = [
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 320 512"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
    </svg>,
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="30px"
      width="30px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
    </svg>, // Record icon for page 1
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 512 512"
      height="28px"
      width="28px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23A19.92 19.92 0 0 0 122 502.31c52.91-23.3 53.59-25.14 62.56-22.7C337.85 521.8 504 423.7 504 248.57 504 110.34 396.59 8 256.55 8zm149.24 185.13l-73 115.57a37.37 37.37 0 0 1-53.91 9.93l-58.08-43.47a15 15 0 0 0-18 0l-78.37 59.44c-10.46 7.93-24.16-4.6-17.11-15.67l73-115.57a37.36 37.36 0 0 1 53.91-9.93l58.06 43.46a15 15 0 0 0 18 0l78.41-59.38c10.44-7.98 24.14 4.54 17.09 15.62z"></path>
    </svg>,
    <svg
      key="icon"
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="30px"
      width="30px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
    </svg>,
  ];



  
  const stats = [
    { platform: "Facebook", totalRecords: countryStats?.facebookStats?.total ?? 0 },
    { platform: "Instagram", totalRecords: countryStats?.instagramStats?.total ?? 0 },
    { platform: "Twitter", totalRecords: countryStats?.messengerStats?.total ?? 0 },
    { platform: "LinkedIn", totalRecords: countryStats?.linkedinStats?.total ?? 0 },
  ];

  const totalUsers =
    (countryStats?.facebookStats?.total || 0) +
    (countryStats?.instagramStats?.total || 0) +
    (countryStats?.messengerStats?.total || 0) +
    (countryStats?.linkedinStats?.total || 0);

    const title = `Social Media Users in ${formattedCountryName || "Country"} - ${formattedMonthName} ${part6} statistics`


    const chartRef = useRef<HTMLDivElement>(null);

    // Function to handle the download of the chart as a PNG image
    const handleDownloadImage = async () => {
      if (chartRef.current === null) {
        return;
      }
  
      try {
        // Convert the chart to a PNG image
        const dataUrl = await toPng(chartRef.current);
  
        // Create a temporary link to download the image
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = "social-media-usage-stats-by-Platform.png"; // File name for the image
        document.body.appendChild(link); // Append the link to the document
        link.click(); // Programmatically click the link to download the image
        document.body.removeChild(link); // Remove the link after download
      } catch (error) {
        console.error("Error converting chart to image", error);
      }
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url ? { "@id": item.url } : undefined,
      })),
    };

  return (
    <>

<Head>
        <meta charSet="UTF-8" />
        <title>{title}</title>

        {/* <BreadcrumbSchema items={breadcrumbItems} /> */}
         {/* Breadcrumb Schema */}
         <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />

        <meta
          property="og:title"
          content={`Social Media Users in ${formattedCountryName || "Country"} - ${formattedMonthName} ${part6}`}
        />
        <meta
          name="twitter:title"
          content={`Social Media Users in ${formattedCountryName || "Country"} - ${formattedMonthName} ${part6}`}
        />

        <meta
          name="description"
          content={`Get social media users stats in ${formattedSlug} for free. The social media demographics data is for ${formattedMonthName} ${part6}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />
        <meta
          property="og:description"
          content={`Get social media users stats in ${formattedSlug} for free. The social media demographics data is for ${formattedMonthName} ${part6}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />
        <meta
          name="twitter:description"
          content={`Get social media users stats in ${formattedSlug} for free. The social media demographics data is for ${formattedMonthName} ${part6}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />

        <meta name="keywords" content={`Most used social media platforms in ${part5} ${part6} ${formattedSlug}, Most used social media platform in ${formattedSlug} ${part6}, Social media users by platform in ${part5} ${part6} in ${formattedSlug}, social media users data ${part5} ${part6} in ${formattedSlug}`} />

        <link rel="canonical" href={`https://www.bizdataset.com/stats/${formattedSlug}/social-media-users-in-${part5}-${part6}`} />
        <meta property="og:url" content={`https://www.bizdataset.com/stats/${formattedSlug}/social-media-users-in-${part5}-${part6}`} />

        <meta property="og:image" content="https://bizdataset-report.s3.eu-north-1.amazonaws.com/asset/favicon-512x512.png" />
        <meta name="twitter:image" content="https://bizdataset-report.s3.eu-north-1.amazonaws.com/asset/favicon-512x512.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bizdataset" />

        <meta name="robots" content="index, follow"></meta>
        <link rel="dns-prefetch" href={`${process.env.SOCIAL_STATS_API_URL}`} />

      </Head>


      {" "}
      <section className="relative w-full ">
        {/* Pseudo-element for background image with reduced opacity */}
        <div className="absolute inset-0 mt-14 sm:w-auto md:w-auto lg:w-auto flex items-center justify-center sm:ml-4 md:ml-12 lg:ml-24 background-image-custom"></div>

        <div className=" lg:mt-30 mx-2 md:mx-4 lg:mx-0">
          <div className="mb-3 py-9 w-full flex mt-10 items-center justify-center  flex-col relative z-10">
            <Breadcrumbs location={location} />
            <div className="flex items-center uppercase justify-center tracking-wide text-[20px] text-[var(--title-color)] font-semibold  mb-4 md:mb-2">
            {formattedMonthName} {part6}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center  mb-4 tracking-wide  text-[var(--title-color)]">
              <span className="gradient-new-text">Social media</span> users in{" "}
              {formattedCountryName || "Country"}
            </h1>
            <div className="text-[var(--text-color)] max-w-[750px] text-[16px] text-center lg:text-[18px] leading-relaxed mb-4">
              <p>
              Instantly access social media demographics of Facebook, Instagram, Messenger, and LinkedIn in {formattedCountryName || "Country"} for {formattedMonthName} {part6}.
              </p>
            </div>
            <SocialCounter
              totalRecords={Number(totalRecords)}
              totalPhoneNumbers={Number(totalPhoneNumbers)}
              totalEmails={Number(totalEmails)}
              totalFacebooks={Number(totalFacebooks)}
              messages={messages}
              showRecords={true}
              showPhoneNumbers={true}
              showEmails={true}
              showFacebook={true}
              icons={page1Icons}
              recordLink={`/social-stats/${formattedSlug}/facebook-users-in-${part5}-${part6}`}
              phoneNumberLink={`/social-stats/${formattedSlug}/instagram-users-in-${part5}-${part6}`}
              emailLink={`/social-stats/${formattedSlug}/messenger-users-in-${part5}-${part6}`}
              facebookLink={`/social-stats/${formattedSlug}/linkedin-users-in-${part5}-${part6}`}
            />
          </div>
        </div>
      </section>

   


      <div className="max-w-[1000px] md:my-16 mb-4 mx-auto p-4 rounded-lg shadow-md">

      <section className="mt-4 mb-4 mx-auto p-6 bg-white" ref={chartRef}>
  <h2 className=" text-xl md:text-3xl font-bold mb-4 text-center"> <span className="gradient-new-text">Social media Usage</span> Stats By Platform</h2>
  {/* <p className="mb-2 text-justify">
    The total number of social media users in <strong className="gradient-new-text">{formattedCountryName || "Country"}</strong> for <strong className="gradient-new-text">{formattedMonthName} {part6}</strong> across top platforms such as Facebook, Instagram, Messenger, and LinkedIn is <strong className="gradient-new-text">{totalUsers.toLocaleString()}</strong>.
    </p>
    <p className="mb-4 text-justify">
    Among them, Facebook has a user base of <strong className="gradient-new-text">{countryStats?.facebookStats?.total?.toLocaleString()}</strong> users. Instagram has a significant user base of <strong className="gradient-new-text">{countryStats?.instagramStats?.total?.toLocaleString()}</strong>, reflecting its popularity among various demographics. Messenger, another widely used communication tool, accounts for <strong className="gradient-new-text">{countryStats?.messengerStats?.total?.toLocaleString()}</strong> users. Lastly, LinkedIn, known for its professional networking focus, has a total of <strong className="gradient-new-text">{countryStats?.linkedinStats?.total?.toLocaleString()}</strong> users.
    These figures highlight the broad distribution of users across different social media platforms.
  </p> */}

     {/* <h1 className="text-2xl font-bold mb-4">Age and Gender Distribution</h1> */}
     <PlatformsChart stats={stats} />
</section>

<button
                onClick={handleDownloadImage}
                className=" px-4 mx-auto  flex item-center justify-center py-2 border border-[#0070f0] text-[#0070f0] graadient-new-text  rounded-md  transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#0070f0"
                  width="20px"
                  height="20px"
                  viewBox="0 0 24 24"
                >
                  <path d="M22.71,6.29a1,1,0,0,0-1.42,0L20,7.59V2a1,1,0,0,0-2,0V7.59l-1.29-1.3a1,1,0,0,0-1.42,1.42l3,3a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l3-3A1,1,0,0,0,22.71,6.29ZM19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h8a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21Z" />
                </svg>
                .PNG
              </button>
   

      <section className="mt-4 mx-auto p-6 bg-white">
  <p className="mb-2 text-justify">
    The total number of social media users in <strong className="gradient-new-text">{formattedCountryName || "Country"}</strong> for <strong className="gradient-new-text">{formattedMonthName} {part6}</strong> across top platforms such as Facebook, Instagram, Messenger, and LinkedIn is <strong className="gradient-new-text">{formatNumberWithSpaces(totalUsers || 0)}</strong>.
    </p>
    <p className=" text-justify">
    Among them, Facebook has a user base of <strong className="gradient-new-text">{formatNumberWithSpaces(countryStats?.facebookStats?.total || 0)}</strong> users. Instagram has a significant user base of <strong className="gradient-new-text">{formatNumberWithSpaces(countryStats?.instagramStats?.total || 0)}</strong>, reflecting its popularity among various demographics. Messenger, another widely used communication tool, accounts for <strong className="gradient-new-text">{formatNumberWithSpaces(countryStats?.messengerStats?.total || 0)}</strong> users. Lastly, LinkedIn, known for its professional networking focus, has a total of <strong className="gradient-new-text">{formatNumberWithSpaces(countryStats?.linkedinStats?.total || 0)}</strong> users.
    These figures highlight the broad distribution of users across different social media platforms.
  </p>
</section>
      

    </div>
    
    </>
  );
};

export default StatsDetail;
